import './enterpriseCountdown.scss';

import React, { useState, useEffect } from 'react';
import LangCache from '../../base/langCache';
import { guideLog } from '../../dataServices/guideLog';
import { PlainText } from '../plainText/plainText';

import { IAccountPlan } from '../../interface/plan';

interface IProps {
    accountPlan: IAccountPlan;
    onExpire(): void;
}

const getDoubleNum = (num: number) => {
    return num > 9 ? `${num}` : `0${num}`;
};

const getRemainingTime = (remainingTime: number) => {
    const oneHour = 60 * 60;
    const oneDayTime = 24 * oneHour;
    const remainingDays = Math.floor(remainingTime / oneDayTime);
    const remainingHours = Math.floor(
        (remainingTime - oneDayTime * remainingDays) / oneHour,
    );
    const remainingMinutes = Math.floor((remainingTime % oneHour) / 60);
    const remainingSeconds = Math.floor(remainingTime % 60);
    return {
        days: remainingDays,
        hours: remainingHours,
        minutes: remainingMinutes,
        seconds: remainingSeconds,
    };
};

const getDate = (time: number, joiner: string = '-') => {
    const date = new Date(time);
    const year = date.getFullYear();
    const month = getDoubleNum(date.getMonth() + 1);
    const day = getDoubleNum(date.getDate());
    return [year, month, day].join(joiner);
};

const timer = 0;
export const EnterpriseCountdown = ({ accountPlan, onExpire }: IProps) => {
    const LANG = LangCache.getLang();
    const [countdownText, setCountdownText] = useState('');
    const deadline = getDate(accountPlan.expire_time * 1000);

    const updateCountdown = () => {
        const currentTime = Date.now() / 1000;
        const remainingTime = accountPlan.expire_time - currentTime;
        if (!remainingTime || remainingTime < 0) {
            clearInterval(timer);
            setCountdownText('');
            onExpire();
            return;
        }
        const {
            days: remainingDays,
            hours,
            minutes,
            seconds,
        } = getRemainingTime(remainingTime);
        const hourText = getDoubleNum(hours);
        const minuteText = getDoubleNum(minutes);
        const secondText = getDoubleNum(seconds);
        const tipKey =
            '剩余<span>{$0}</span>天<span>{$1}</span>时<span>{$2}</span>分<span>{$3}</span>秒';
        const newCountdownText = LANG[tipKey]
            .replace('{$0}', `${remainingDays}`)
            .replace('{$1}', hourText)
            .replace('{$2}', minuteText)
            .replace('{$3}', secondText);

        setCountdownText(newCountdownText);
    };

    useEffect(() => {
        const timer = setInterval(updateCountdown, 1000);
        return () => {
            clearInterval(timer);
        };
    }, [accountPlan]);

    const doJump = (href: string, target = '_self') => {
        return () => window.open(href, target);
    };

    const goAddAccount = () => {
        const href = `${location.origin}/platformManage/index#/list?step-guide=3`;
        const action = doJump(href);
        guideLog({ page_type: 1, action_type: 2 }).then(action, action);
    };

    const goSales = () => {
        const href = `${location.origin}/cms/contact-sales`;
        const action = doJump(href, '_blank');
        guideLog({ page_type: 1, action_type: 3 }).then(action, action);
    };

    const goBuy = () => {
        const href = `${location.origin}/plan/pricing`;
        const action = doJump(href);
        guideLog({ page_type: 1, action_type: 4 }).then(action, action);
    };

    if (!countdownText) {
        return null;
    }
    const descKey =
        '您的团队版试用将于 {$0} 到期，为保证继续使用，请购买正式版。';
    const descText = LANG[descKey].replace('{$0}', deadline);
    const accountKey = '您可以免费添加{$0}个账户共同试用。';
    const accountText = LANG[accountKey].replace(
        '{$0}',
        `${accountPlan.account_quota}`,
    );
    return (
        <div className="enterprise-countdown">
            <div className="countdown">
                <i className="icon-clock"></i>
                <div dangerouslySetInnerHTML={{ __html: countdownText }}></div>
            </div>
            <div className="expiration-reminder">
                <div className="remaining-account">{accountText}</div>
                <div className="btn-container">
                    <button
                        className="button btn-ghost-grey"
                        onClick={goAddAccount}
                    >
                        <PlainText>添加账户</PlainText>
                    </button>
                </div>
                <div className="remaining-desc">{descText}</div>
                <div className="btn-container">
                    <button className="button btn-ghost" onClick={goSales}>
                        <PlainText>联系销售</PlainText>
                    </button>
                    <button className="button btn-primary" onClick={goBuy}>
                        <PlainText>立即购买</PlainText>
                    </button>
                </div>
            </div>
        </div>
    );
};
